// `/app/developers` (WEB-121): "Webhooks e API" com três abas por `?tab=` (default
// `webhooks`), no desenho do sistema (WEB-133). Substitui as quatro rotas antigas (`api-keys`, `webhooks`,
// `webhooks/deliveries`, `docs`), que viram redirect (arquivos irmãos desta rota).
//
// A camada de dado é a mesma de sempre (`api/queries/webhooks.ts`, `api/queries/api-keys.ts`,
// `features/{webhooks,api-keys}/*`); só a apresentação de lista trocou (tabela -> cartão/linha
// do sistema, `features/developers/*`) e as três telas viraram abas de uma rota só. Os drawers
// (criar/editar/detalhe/rotate/ping de endpoint; detalhe/reenvio de entrega; criar/revelar/
// rotacionar/revogar chave) são os MESMOS componentes de antes, sem mudança de comportamento.
//
// `id` = endpoint aberto no drawer (preservado do redirect de `/webhooks`); `delivery` = a
// entrega aberta (era `id` em `/webhooks/deliveries`, renomeado aqui para não colidir com o
// `id` do endpoint agora que as duas telas dividem uma URL só - Questões abertas).

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  API_KEYS_QUERY_KEY,
  type ApiKey,
  apiKeysQueryOptions,
  createApiKey,
  revokeApiKey,
  rotateApiKey,
} from '../api/queries/api-keys.ts';
import { profileQueryOptions } from '../api/queries/overview.ts';
import {
  createEndpoint,
  DELIVERIES_QUERY_KEY,
  deleteEndpoint,
  deliveriesListOptions,
  deliveryOptions,
  ENDPOINTS_QUERY_KEY,
  endpointsQueryOptions,
  patchEndpoint,
  pingEndpoint,
  retryDelivery,
  rotateEndpointSecret,
  rotatePostbackSecret,
  type WebhookEndpoint,
} from '../api/queries/webhooks.ts';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { ListSkeleton } from '../components/app/list-skeleton.tsx';
import { ListToolbar } from '../components/app/list-toolbar.tsx';
import { Notice } from '../components/app/notice.tsx';
import { KeyRound, Search, Send, Webhook, X } from '../components/icons/index.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardTitle } from '../components/ui/card.tsx';
import { fieldShell, Input, InputAddon, InputGroup } from '../components/ui/input.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import type { ApiKeyScope } from '../features/api-keys/model.ts';
import {
  activeCount,
  isStepUpCancelled as isKeyStepUpCancelled,
  keyErrorMessage,
  keysOfMode,
  MAX_ACTIVE_KEYS_PER_MODE,
} from '../features/api-keys/model.ts';
import { NewKeyDrawer } from '../features/api-keys/new-key-drawer.tsx';
import { type RevealedKey, RevealKeyDrawer } from '../features/api-keys/reveal-key-drawer.tsx';
import { RevokeKeyDrawer } from '../features/api-keys/revoke-key-drawer.tsx';
import { ApiKeyRow } from '../features/developers/api-key-row.tsx';
import { DeliveryRow } from '../features/developers/delivery-row.tsx';
import { DevelopersScreen, type DevTab } from '../features/developers/developers-screen.tsx';
import { DocsTab } from '../features/developers/docs-tab.tsx';
import { EndpointCard } from '../features/developers/endpoint-card.tsx';
import { SectionHead } from '../features/developers/section-head.tsx';
import { DeliveryDrawer } from '../features/webhooks/delivery-drawer.tsx';
import {
  EndpointDetailDrawer,
  EndpointFormDrawer,
  type EndpointFormValue,
} from '../features/webhooks/endpoint-drawers.tsx';
import {
  deliveryAttemptsOptions,
  endpointFailures24hOptions,
  endpointStatsOptions,
  failureBadgeLabel,
  failuresByEndpoint,
  lastDeliveryOptions,
} from '../features/webhooks/local-queries.ts';
import {
  autoDisabledTitle,
  DELIVERY_STATUSES,
  DELIVERY_TARGETS,
  type DeliveryTarget,
  endpointErrorMessage,
  isAutoDisabled,
  isStepUpCancelled,
  MAX_ENDPOINTS,
  matchesSearch,
  pingSentMessage,
  retryErrorMessage,
  TARGET_FILTER_LABEL,
} from '../features/webhooks/model.ts';
import { cn } from '../lib/cn.ts';

/** Esqueleto com o formato do `EndpointCard`: selo + URL, chips de evento, três células. */
function EndpointSkeleton() {
  return (
    <div className="grid gap-4 rounded-2xl bg-card p-5 shadow-(--shadow-card)">
      <div className="flex items-start justify-between gap-4">
        <div className="grid min-w-0 flex-1 gap-3">
          <Skeleton className="h-[22px] w-24 rounded-full" />
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
        <Skeleton className="hidden h-8 w-48 rounded-full sm:block" />
      </div>
      <div className="flex gap-1.5">
        <Skeleton className="h-6 w-32 rounded-full" />
        <Skeleton className="h-6 w-28 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <Skeleton className="h-[68px] rounded-lg" />
        <Skeleton className="h-[68px] rounded-lg" />
        <Skeleton className="col-span-2 h-[68px] rounded-lg sm:col-span-1" />
      </div>
    </div>
  );
}

/** Quantas entregas do endpoint entram na linha do tempo do drawer (croqui: 3). */
const TIMELINE_SIZE = 3;
const ALL_TARGETS = 'all';

const searchSchema = z.object({
  tab: z.enum(['keys', 'webhooks', 'docs']).optional().catch(undefined),
  /** Endpoint aberto no drawer de detalhe (aba Webhooks). */
  id: z.string().min(1).optional().catch(undefined),
  /** Entrega aberta no drawer de detalhe (aba Webhooks, seção Entregas). */
  delivery: z.string().min(1).optional().catch(undefined),
  status: z.enum(DELIVERY_STATUSES).optional().catch(undefined),
  /** Filtra as entregas por um endpoint (chip removível, MAPA item 7). */
  endpoint_id: z.string().min(1).optional().catch(undefined),
  target: z.enum(DELIVERY_TARGETS).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

type DevelopersSearch = z.infer<typeof searchSchema>;

function bodyOf(value: EndpointFormValue) {
  return {
    url: value.url,
    events: value.events,
    ...(value.description === '' ? {} : { description: value.description }),
  };
}

function targetOf(value: string): DeliveryTarget | undefined {
  return DELIVERY_TARGETS.find((target) => target === value);
}

/** Aba Webhooks: endpoints + entregas recentes (MAPA itens 1 a 7). */
function WebhooksTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { mode } = useMode();
  const queryClient = useQueryClient();

  const endpoints = useQuery(endpointsQueryOptions(mode));
  const rows = endpoints.data ?? [];

  const stats = useQueries({
    queries: rows.map((endpoint) => endpointStatsOptions(mode, endpoint.id)),
    combine: (results) =>
      new Map(
        results.flatMap((result, index) => {
          const id = rows[index]?.id;
          return result.data === undefined || id === undefined ? [] : [[id, result.data] as const];
        }),
      ),
  });
  const lastDeliveries = useQueries({
    queries: rows.map((endpoint) => lastDeliveryOptions(mode, endpoint.id)),
    combine: (results) =>
      new Map(
        results.flatMap((result, index) => {
          const id = rows[index]?.id;
          return result.data === undefined || id === undefined ? [] : [[id, result.data] as const];
        }),
      ),
  });
  const failures24h = useQuery(endpointFailures24hOptions(mode));
  const failing = failuresByEndpoint(failures24h.data ?? []);
  const totalFailing = failures24h.data?.length ?? 0;

  const selected = rows.find((endpoint) => endpoint.id === search.id);
  const deliveries = useInfiniteQuery({
    ...deliveriesListOptions(mode, { endpoint_id: search.id }),
    enabled: search.id !== undefined,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | undefined>(undefined);
  const [createdSecret, setCreatedSecret] = useState<string | undefined>(undefined);
  const [rotated, setRotated] = useState<
    { secret: string; previousExpiresAt?: string } | undefined
  >(undefined);
  const [pingMessage, setPingMessage] = useState<string | undefined>(undefined);

  const openDetail = (endpoint: WebhookEndpoint | undefined): void => {
    setRotated(undefined);
    setPingMessage(undefined);
    ping.reset();
    rotate.reset();
    reactivate.reset();
    pause.reset();
    remove.reset();
    void navigate({ search: { ...search, id: endpoint?.id }, replace: true });
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [ENDPOINTS_QUERY_KEY] });
  };

  const save = useMutation({
    mutationFn: async (value: EndpointFormValue) => {
      const body = bodyOf(value);
      if (editing !== undefined) {
        await patchEndpoint(mode, editing.id, {
          ...body,
          description: value.description === '' ? null : value.description,
        });
        return undefined;
      }
      return createEndpoint(mode, body);
    },
    onSuccess: async (created) => {
      if (created === undefined) {
        setFormOpen(false);
      } else {
        setCreatedSecret(created.secret);
      }
      await refresh();
    },
  });

  const settled = { onSettled: refresh };

  const ping = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => {
      setPingMessage(undefined);
      return pingEndpoint(mode, endpoint.id);
    },
    onSuccess: (response) => {
      setPingMessage(pingSentMessage(response.delivery_id));
    },
    ...settled,
  });

  const rotate = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => rotateEndpointSecret(mode, endpoint.id),
    onSuccess: (response) => {
      setRotated({
        secret: response.secret,
        previousExpiresAt: response.previous_secret_expires_at,
      });
    },
    ...settled,
  });

  /** `PATCH { enabled: true }` grava `disabled_reason = null` no servidor (API-25). */
  const reactivate = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) =>
      patchEndpoint(mode, endpoint.id, { enabled: true }),
    ...settled,
  });

  /** "Pausar" do card pião (MAPA item 6): o mesmo `PATCH`, com `enabled: false`. Não é
   * reescrita de mutation nenhuma, é o campo oposto da que já existe. */
  const pause = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) =>
      patchEndpoint(mode, endpoint.id, { enabled: false }),
    ...settled,
  });

  const remove = useMutation({
    mutationFn: async (endpoint: WebhookEndpoint) => deleteEndpoint(mode, endpoint.id),
    onSuccess: () => {
      openDetail(undefined);
    },
    ...settled,
  });

  const rotatePostback = useMutation({
    mutationFn: async () => rotatePostbackSecret(mode),
  });

  const autoDisabled = rows.filter(isAutoDisabled);
  const atLimit = rows.length >= MAX_ENDPOINTS;

  const openForm = (endpoint?: WebhookEndpoint): void => {
    save.reset();
    setCreatedSecret(undefined);
    setEditing(endpoint);
    setFormOpen(true);
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setCreatedSecret(undefined);
  };

  const actionError = [ping.error, rotate.error, reactivate.error, pause.error, remove.error].find(
    (error) => error !== null,
  );
  /** Aggrega os `isPending` das mutations do endpoint SELECIONADO (drawer de detalhe);
   * cada card usa a mesma checagem por `.variables?.id`, sem um `busy` global (evita que a
   * ação de um endpoint "destrave" visualmente a de outro que ainda está em voo). */
  const isBusyFor = (endpointId: string | undefined): boolean =>
    endpointId !== undefined &&
    [ping, rotate, reactivate, pause, remove].some(
      (mutation) => mutation.isPending && mutation.variables?.id === endpointId,
    );

  // --- entregas recentes ---
  const term = search.q ?? '';
  const deliveriesFiltered =
    search.status !== undefined ||
    search.target !== undefined ||
    search.endpoint_id !== undefined ||
    term.trim() !== '';
  const list = useInfiniteQuery(
    deliveriesListOptions(mode, { status: search.status, endpoint_id: search.endpoint_id }),
  );
  const loaded = (list.data?.pages ?? []).flatMap((page) => page.data);
  const deliveryRows = loaded.filter(
    (row) =>
      (search.target === undefined || row.target === search.target) && matchesSearch(row, term),
  );
  const selectedDelivery = loaded.find((row) => row.id === search.delivery);
  const filteredEndpoint = rows.find((endpoint) => endpoint.id === search.endpoint_id);
  const deliveryDetail = useQuery(deliveryOptions(mode, search.delivery));
  const deliveryAttempts = useQuery(
    deliveryAttemptsOptions(mode, deliveryDetail.data?.event_id, deliveryDetail.data?.endpoint_id),
  );

  const deliveryTab = search.status === 'failed' ? 'fail' : 'all';

  const applySearch = (next: Partial<DevelopersSearch>): void => {
    void navigate({ search: { ...search, ...next }, replace: true });
  };

  const [resendMessage, setResendMessage] = useState<{ ok: boolean; text: string } | undefined>(
    undefined,
  );
  const resend = useMutation({
    mutationFn: async (row: { id: string }) => retryDelivery(mode, row.id),
    onSuccess: (response) => {
      setResendMessage({
        ok: true,
        text: `Reenvio enfileirado (${response.delivery_id}). A nova tentativa aparece na lista em alguns segundos.`,
      });
    },
    onError: (error) => {
      if (!isStepUpCancelled(error)) {
        setResendMessage({ ok: false, text: retryErrorMessage(error) });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [DELIVERIES_QUERY_KEY] });
    },
  });

  return (
    <>
      <SectionHead
        title="Endpoints"
        hint="Recebem um POST assinado a cada evento dos seus PIX."
        action={
          <Button
            disabled={atLimit}
            onClick={() => {
              openForm();
            }}
          >
            <Webhook className="size-4" aria-hidden="true" />
            Adicionar endpoint
          </Button>
        }
      />

      {autoDisabled.length > 0 && (
        <Notice
          tone="danger"
          title={autoDisabledTitle(autoDisabled.length)}
          sub="50 entregas dead consecutivas sem sucesso em 7 dias. Corrija o endpoint e reative manualmente."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                openDetail(autoDisabled[0]);
              }}
            >
              Ver qual
            </Button>
          }
        />
      )}

      {endpoints.error !== null && endpoints.error !== undefined && endpoints.data === undefined ? (
        <ErrorState
          error={endpoints.error}
          onRetry={() => {
            void endpoints.refetch();
          }}
        />
      ) : endpoints.isPending ? (
        <div
          role="status"
          aria-busy="true"
          aria-label="Carregando endpoints"
          className="grid gap-4"
        >
          {[0, 1].map((key) => (
            <EndpointSkeleton key={key} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Webhook}
          title="Nenhum endpoint cadastrado"
          description="Crie um pra receber transaction.paid e os demais eventos em tempo real."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                openForm();
              }}
            >
              Cadastrar o primeiro endpoint
            </Button>
          }
        />
      ) : (
        rows.map((endpoint, index) => (
          <EndpointCard
            key={endpoint.id}
            index={index}
            endpoint={endpoint}
            failing={failing.get(endpoint.id) ?? 0}
            successRate={stats.get(endpoint.id)?.success_rate_7d}
            lastDelivery={lastDeliveries.get(endpoint.id)}
            pinging={ping.isPending && ping.variables?.id === endpoint.id}
            toggling={
              (reactivate.isPending && reactivate.variables?.id === endpoint.id) ||
              (pause.isPending && pause.variables?.id === endpoint.id)
            }
            onPing={() => {
              ping.mutate(endpoint);
            }}
            onToggleActive={() => {
              if (endpoint.enabled) {
                pause.mutate(endpoint);
              } else {
                reactivate.mutate(endpoint);
              }
            }}
            onOpenDetail={() => {
              openDetail(endpoint);
            }}
            onFilterDeliveries={() => {
              applySearch({ endpoint_id: endpoint.id });
            }}
          />
        ))
      )}

      <div className="flex items-center justify-between gap-3 px-1 text-12 text-muted-foreground">
        <span>máx. {MAX_ENDPOINTS} endpoints por modo</span>
        <span className="hidden sm:inline">
          URL precisa ser https e pública, sem IP privado, localhost ou metadata da nuvem
        </span>
      </div>

      {/* Fora do desenho original: o segredo do `postback_url` da cobrança (`spec/07` §4.2)
          só tem rotação nesta tela (WEB-57). */}
      <Card>
        <CardContent className="grid gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="grid gap-1">
              <CardTitle>Postback da loja</CardTitle>
              <CardDescription>
                O segredo que assina os postbacks enviados ao postback_url da cobrança
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="self-start sm:self-auto"
              loading={rotatePostback.isPending}
              onClick={() => {
                rotatePostback.mutate();
              }}
            >
              {rotatePostback.isPending ? 'Rotacionando…' : 'Rotacionar segredo do postback'}
            </Button>
          </div>
          {rotatePostback.error !== null && !isStepUpCancelled(rotatePostback.error) && (
            <p role="alert" className="text-12 text-destructive">
              {endpointErrorMessage(rotatePostback.error)}
            </p>
          )}
          {rotatePostback.data !== undefined && (
            <p
              role="status"
              className="rounded-lg bg-secondary px-3.5 py-3 font-mono text-13 break-all select-all"
            >
              {rotatePostback.data.secret}
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs
        value={deliveryTab}
        onValueChange={(next) => {
          applySearch({ status: next === 'fail' ? 'failed' : undefined });
        }}
        className="mt-2 gap-4"
      >
        <SectionHead
          title="Entregas recentes"
          hint="Falhas são reenviadas sozinhas por até 24 h."
          action={
            <TabsList aria-label="Filtrar entregas" className="w-full sm:w-fit">
              <TabsTrigger value="all">Todas</TabsTrigger>
              <TabsTrigger value="fail">Falhas · {failureBadgeLabel(totalFailing)}</TabsTrigger>
            </TabsList>
          }
        />

        <TabsContent value={deliveryTab} className="grid gap-4">
          {/* Busca e alvo continuam client-side, sobre a página carregada. "Mortas"
            (`status=dead`) só chega por deep link. */}
          <ListToolbar>
            <InputGroup className="w-full sm:max-w-80 sm:flex-1">
              <InputAddon>
                <Search aria-hidden="true" />
              </InputAddon>
              <Input
                type="text"
                aria-label="Buscar entregas"
                placeholder="Buscar por evento ou id da entrega"
                value={term}
                onChange={(event) => {
                  applySearch({ q: event.target.value === '' ? undefined : event.target.value });
                }}
              />
            </InputGroup>
            <select
              aria-label="Alvo"
              value={search.target ?? ALL_TARGETS}
              onChange={(event) => {
                applySearch({ target: targetOf(event.target.value) });
              }}
              className={cn(fieldShell, 'w-full cursor-pointer px-3 sm:w-auto')}
            >
              <option value={ALL_TARGETS}>Todos os alvos</option>
              {DELIVERY_TARGETS.map((target) => (
                <option key={target} value={target}>
                  {TARGET_FILTER_LABEL[target]}
                </option>
              ))}
            </select>
            {filteredEndpoint !== undefined && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="max-w-full"
                onClick={() => {
                  applySearch({ endpoint_id: undefined });
                }}
              >
                <span className="truncate">
                  endpoint: {filteredEndpoint.description ?? filteredEndpoint.url}
                </span>
                <X aria-hidden="true" />
              </Button>
            )}
          </ListToolbar>

          {list.error !== null && list.error !== undefined && list.data === undefined ? (
            <ErrorState
              error={list.error}
              onRetry={() => {
                void list.refetch();
              }}
            />
          ) : (
            <Card className="gap-0 py-0">
              {list.isPending ? (
                <div role="status" aria-busy="true" aria-label="Carregando entregas">
                  <ListSkeleton rows={4} bare />
                </div>
              ) : deliveryRows.length === 0 ? (
                <EmptyState
                  bare
                  icon={Send}
                  title={
                    deliveriesFiltered ? 'Nenhuma entrega com esse filtro' : 'Nenhuma entrega ainda'
                  }
                  description={
                    deliveriesFiltered
                      ? 'Troque o filtro ou limpe a busca para ver as outras entregas.'
                      : 'Cada evento enviado aos seus endpoints aparece aqui. Um teste de um endpoint cria a primeira.'
                  }
                  action={
                    deliveriesFiltered && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          applySearch({
                            status: undefined,
                            target: undefined,
                            q: undefined,
                            endpoint_id: undefined,
                          });
                        }}
                      >
                        Limpar filtros
                      </Button>
                    )
                  }
                />
              ) : (
                deliveryRows.map((delivery) => (
                  <DeliveryRow
                    key={delivery.id}
                    delivery={delivery}
                    onSelect={() => {
                      setResendMessage(undefined);
                      applySearch({ delivery: delivery.id });
                    }}
                  />
                ))
              )}
            </Card>
          )}

          {list.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                loading={list.isFetchingNextPage}
                onClick={() => {
                  void list.fetchNextPage();
                }}
              >
                {list.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <EndpointFormDrawer
        open={formOpen}
        onClose={closeForm}
        endpoint={editing}
        isPending={save.isPending}
        error={save.error}
        createdSecret={createdSecret}
        onSubmit={(value) => {
          save.mutate(value);
        }}
      />

      <EndpointDetailDrawer
        endpoint={formOpen ? undefined : selected}
        stats={search.id === undefined ? undefined : stats.get(search.id)}
        deliveries={(deliveries.data?.pages[0]?.data ?? []).slice(0, TIMELINE_SIZE)}
        deliveriesLoading={deliveries.isPending && search.id !== undefined}
        onClose={() => {
          openDetail(undefined);
        }}
        onPing={(endpoint) => {
          ping.mutate(endpoint);
        }}
        onRotate={(endpoint) => {
          rotate.mutate(endpoint);
        }}
        onEdit={openForm}
        onDelete={(endpoint) => {
          remove.mutate(endpoint);
        }}
        onReactivate={(endpoint) => {
          reactivate.mutate(endpoint);
        }}
        busy={isBusyFor(selected?.id)}
        message={pingMessage}
        error={actionError}
        rotatedSecret={rotated}
      />

      <DeliveryDrawer
        open={search.delivery !== undefined}
        row={selectedDelivery}
        detail={deliveryDetail.data}
        attempts={deliveryAttempts.data ?? []}
        isLoading={deliveryDetail.isPending && search.delivery !== undefined}
        error={deliveryDetail.error}
        onRetryLoad={() => {
          void deliveryDetail.refetch();
        }}
        onClose={() => {
          setResendMessage(undefined);
          applySearch({ delivery: undefined });
        }}
        onResend={(row) => {
          resend.mutate(row);
        }}
        isResending={resend.isPending}
        {...(resendMessage === undefined ? {} : { resendMessage })}
      />
    </>
  );
}

/** Aba Chaves de API (MAPA item "chave: nome, ambiente..." e "aviso dourado"). */
function KeysTab() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(apiKeysQueryOptions());
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<RevealedKey | undefined>(undefined);
  const [toRevoke, setToRevoke] = useState<ApiKey | undefined>(undefined);
  const [rowErrorId, setRowErrorId] = useState<string | undefined>(undefined);
  const [rowErrorMsg, setRowErrorMsg] = useState<string | undefined>(undefined);
  const [busyKeyId, setBusyKeyId] = useState<string | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [API_KEYS_QUERY_KEY] });
  };

  const create = useMutation({
    gcTime: 0,
    mutationFn: async ({ name, scopes }: { name: string; scopes: ApiKeyScope[] }) =>
      createApiKey({ name, mode, scopes }),
    onSuccess: (created) => {
      setCreateOpen(false);
      setRevealed(created);
      void refresh();
    },
  });

  const rotate = useMutation({
    gcTime: 0,
    mutationFn: async (key: ApiKey) => {
      setBusyKeyId(key.id);
      setRowErrorId(undefined);
      return rotateApiKey(key.id);
    },
    onSuccess: (rotated) => {
      setRevealed(rotated);
    },
    onError: (error, key) => {
      if (!isKeyStepUpCancelled(error)) {
        setRowErrorId(key.id);
        setRowErrorMsg(keyErrorMessage(error, mode));
      }
    },
    onSettled: async () => {
      setBusyKeyId(undefined);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: async (key: ApiKey) => {
      setRowErrorId(undefined);
      return revokeApiKey(key.id);
    },
    onSuccess: () => {
      setToRevoke(undefined);
    },
    onError: (error, key) => {
      setToRevoke(undefined);
      if (!isKeyStepUpCancelled(error)) {
        setRowErrorId(key.id);
        setRowErrorMsg(keyErrorMessage(error, mode));
      }
    },
    onSettled: refresh,
  });

  const closeReveal = (): void => {
    setRevealed(undefined);
    create.reset();
    rotate.reset();
  };

  const all = keys.data ?? [];
  const rows = keysOfMode(all, mode);
  const active = activeCount(all, mode, new Date());
  const atLimit = active >= MAX_ACTIVE_KEYS_PER_MODE;
  const kycApproved = profile.data === undefined || profile.data.kyc_status === 'approved';
  const modeNoun = mode === 'live' ? 'produção' : 'teste';

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <SectionHead
        title="Chaves de API"
        hint="Autenticam as chamadas do seu servidor. Criar, rotacionar e revogar pedem confirmação em duas etapas."
        action={
          <Button disabled={atLimit} onClick={openCreate}>
            <KeyRound className="size-4" aria-hidden="true" />
            Criar chave
          </Button>
        }
      />

      <Notice
        tone="warning"
        title="A chave secreta age na sua conta com as permissões dela."
        sub="Use só no servidor, nunca no navegador ou no app, e revogue na hora se ela vazar."
      />

      {mode === 'live' && !kycApproved && (
        <Notice
          tone="warning"
          title="Verificação pendente."
          sub="Criar chave em produção exige verificação aprovada; a API recusa com 403 kyc_required."
        />
      )}

      {keys.error !== null && keys.error !== undefined && keys.data === undefined ? (
        <ErrorState
          error={keys.error}
          onRetry={() => {
            void keys.refetch();
          }}
        />
      ) : keys.isPending ? (
        <Card className="gap-0 py-0">
          <div role="status" aria-busy="true" aria-label="Carregando chaves">
            <ListSkeleton rows={3} bare />
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title={`Nenhuma chave de ${modeNoun}`}
          description="O segredo aparece uma única vez, na criação."
          action={
            <Button size="sm" onClick={openCreate}>
              Criar a primeira chave
            </Button>
          }
        />
      ) : (
        <Card className="rise-in gap-0 py-0" style={{ animationDelay: '80ms' }}>
          <ul className="divide-y divide-border">
            {rows.map((key) => (
              <li key={key.id}>
                <ApiKeyRow
                  apiKey={key}
                  busy={busyKeyId === key.id}
                  error={rowErrorId === key.id ? rowErrorMsg : undefined}
                  onRotate={() => {
                    rotate.mutate(key);
                  }}
                  onRevoke={() => {
                    setToRevoke(key);
                  }}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3 px-1 text-12 text-muted-foreground">
        <span className="data-value">
          {active} de {MAX_ACTIVE_KEYS_PER_MODE} chaves de {modeNoun}
        </span>
        {atLimit && <span>limite atingido · revogue uma pra criar outra</span>}
      </div>

      <NewKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode={mode}
        kycApproved={kycApproved}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(name, scopes) => {
          create.mutate({ name, scopes });
        }}
      />

      <RevealKeyDrawer secret={revealed} onClose={closeReveal} />

      <RevokeKeyDrawer
        apiKey={toRevoke}
        onOpenChange={(open) => {
          if (!open) {
            setToRevoke(undefined);
          }
        }}
        isPending={revoke.isPending}
        onConfirm={(key) => {
          revoke.mutate(key);
        }}
      />
    </>
  );
}

function Developers() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { mode } = useMode();
  const tab: DevTab = search.tab ?? 'webhooks';
  // Mesma `queryKey` de `WebhooksTab`: o React Query dedupe a chamada de rede, então o
  // badge da aba fica certo mesmo fora da aba Webhooks sem custar um segundo fetch.
  const failures24h = useQuery(endpointFailures24hOptions(mode));

  return (
    <DevelopersScreen
      tab={tab}
      webhooksBadge={failures24h.data?.length ?? 0}
      onTabChange={(next) => {
        void navigate({ search: { tab: next }, replace: true });
      }}
    >
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'keys' && <KeysTab />}
      {tab === 'docs' && <DocsTab />}
    </DevelopersScreen>
  );
}

export const Route = createFileRoute('/app/developers/')({
  validateSearch: searchSchema,
  component: Developers,
});
